import {
  BLINK_CLIENT_KEY_HEADER,
  BlinkInstance,
  BlinksRegistry,
  clientKey,
  defaultBlinkSupportStrategy,
  getBlinkSupportabilityMetadata,
  proxifyMetadata,
  shouldIgnoreProxy,
  shouldPreserveOriginal,
  unfurlUrlToBlinkApiUrl
} from "./chunk-5XH3SWXK.js";

// src/service-api/useBlink.ts
import { useCallback as useCallback4, useEffect as useEffect6, useState as useState6 } from "react";

// src/hooks/useBlink.ts
import { useCallback, useEffect as useEffect3, useMemo, useState as useState3 } from "react";

// src/hooks/useBlinkApiUrl.ts
import { useEffect, useState } from "react";
function useBlinkApiUrl(url) {
  const [apiUrl, setApiUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    let ignore = false;
    if (!url) {
      setApiUrl(null);
      return;
    }
    setIsLoading(true);
    unfurlUrlToBlinkApiUrl(new URL(url)).then((apiUrl2) => {
      if (ignore) {
        return;
      }
      setIsLoading(false);
      setApiUrl(apiUrl2);
    }).catch((e) => {
      console.error(
        "[@dialectlabs/blinks-core] Failed to unfurl blink URL",
        e
      );
      if (!ignore) {
        setApiUrl(null);
      }
    }).finally(() => {
      if (!ignore) {
        setIsLoading(false);
      }
    });
    return () => {
      ignore = true;
    };
  }, [url]);
  return { actionApiUrl: apiUrl, blinkApiUrl: apiUrl, isUrlLoading: isLoading };
}

// src/hooks/useBlinksRegistryInterval.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
function useBlinksRegistryInterval() {
  const [isRegistryLoaded, setRegistryLoaded] = useState2(false);
  useEffect2(() => {
    const registry = BlinksRegistry.getInstance();
    registry.init().then(() => {
      setRegistryLoaded(true);
    });
    return () => {
      registry.stopRefresh();
    };
  }, [isRegistryLoaded]);
  return { isRegistryLoaded };
}

// src/hooks/useBlink.ts
function useBlink2({
  url,
  supportStrategy = defaultBlinkSupportStrategy
}) {
  const { isRegistryLoaded } = useBlinksRegistryInterval();
  const {
    blink: blinkJson,
    isLoading,
    metadata,
    refresh,
    blinkApiUrl,
    originalBlinkApiUrl
  } = useBlink({ url: url.toString() });
  const blink = useMemo(
    () => isRegistryLoaded && blinkApiUrl && blinkJson && metadata ? BlinkInstance.hydrate(
      blinkApiUrl,
      blinkJson,
      metadata,
      supportStrategy
    ) : null,
    [blinkApiUrl, blinkJson, metadata, supportStrategy, isRegistryLoaded]
  );
  return {
    blink,
    blinkJson,
    metadata,
    blinkApiUrl,
    originalBlinkApiUrl,
    isLoading: isLoading || !isRegistryLoaded,
    refresh
  };
}
function useAction({
  url,
  supportStrategy = defaultBlinkSupportStrategy
}) {
  const { isRegistryLoaded } = useBlinksRegistryInterval();
  const { blinkApiUrl, isUrlLoading } = useBlinkApiUrl(url);
  const [blink, setBlink] = useState3(null);
  const [isLoading, setIsLoading] = useState3(false);
  const [hasFetched, setHasFetched] = useState3(false);
  const fetchAction = useCallback(() => {
    if (!blinkApiUrl) {
      return () => {
      };
    }
    let ignore = false;
    setIsLoading(true);
    setHasFetched(false);
    BlinkInstance.fetch(blinkApiUrl, supportStrategy).then((action) => {
      if (!ignore) {
        setIsLoading(false);
        setBlink(action);
        setHasFetched(true);
      }
    }).catch((e) => {
      if (!ignore) {
        console.error("[@dialectlabs/blinks-core] Failed to fetch action", e);
        setBlink(null);
      }
    }).finally(() => {
      if (!ignore) {
        setIsLoading(false);
      }
    });
    return () => {
      ignore = true;
    };
  }, [blinkApiUrl, supportStrategy]);
  useEffect3(() => {
    if (!isRegistryLoaded) {
      return;
    }
    const cleanup = fetchAction();
    return () => {
      cleanup();
    };
  }, [blinkApiUrl, isRegistryLoaded]);
  useEffect3(() => {
    if (!blink || !hasFetched) {
      return;
    }
    try {
      const updated = blink.withUpdate({
        supportStrategy
      });
      setBlink(updated);
    } catch (e) {
      console.error("[@dialectlabs/blinks-core] Failed to update action", e);
    }
  }, [supportStrategy, hasFetched]);
  return {
    // NOTE: this will be removed in the nearest future
    action: blink,
    blink,
    isLoading: !isRegistryLoaded || isUrlLoading || isLoading,
    refresh: fetchAction
  };
}

// src/hooks/useBlinkList.ts
import { useCallback as useCallback2, useEffect as useEffect4, useState as useState4 } from "react";
var useBlinkList = ({ id, wallet }) => {
  const [loading, setLoading] = useState4(false);
  const [data, setData] = useState4();
  const refetch = useCallback2(() => {
    let ignore = false;
    setLoading(true);
    fetchBlinkList(id, wallet).then((data2) => {
      if (!ignore) {
        setData(data2);
      }
    }).finally(() => {
      if (!ignore) {
        setLoading(false);
      }
    });
    return () => {
      ignore = true;
    };
  }, [id, wallet]);
  useEffect4(() => {
    const cancel = refetch();
    return () => {
      cancel();
    };
  }, [refetch]);
  return {
    loading,
    refetch,
    data: data?.entries ?? []
  };
};
async function fetchBlinkList(id, wallet) {
  try {
    const urlObj = new URL(
      `https://registry.dial.to/v1/private/blink-lists/${id}`
    );
    if (wallet) {
      urlObj.searchParams.append("account", wallet);
    }
    const response = await fetch(urlObj, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
      }
    });
    if (!response.ok) {
      console.error(
        `[@dialectlabs/blinks] Failed to fetch blink list, response status: ${response.status}`
      );
      return {
        entries: []
      };
    }
    return await response.json();
  } catch (e) {
    console.error(`[@dialectlabs/blinks] Failed to fetch blink list`, e);
    return {
      entries: []
    };
  }
}

// src/hooks/useMetadata.ts
import { useCallback as useCallback3, useEffect as useEffect5, useState as useState5 } from "react";
var useMetadata = ({ url, wallet }) => {
  const [loading, setLoading] = useState5(false);
  const [data, setData] = useState5();
  const refetch = useCallback3(() => {
    let ignore = false;
    setLoading(true);
    fetchMetadata(url, wallet).then((data2) => {
      if (!ignore) {
        setData(data2);
      }
    }).finally(() => {
      if (!ignore) {
        setLoading(false);
      }
    });
    return () => {
      ignore = true;
    };
  }, [url, wallet]);
  useEffect5(() => {
    const cancel = refetch();
    return () => {
      cancel();
    };
  }, [refetch]);
  return {
    loading,
    refetch,
    data
  };
};
async function fetchMetadata(url, wallet) {
  try {
    const urlObj = new URL(url);
    if (wallet) {
      urlObj.searchParams.append("account", wallet);
    }
    const { url: proxyUrl, headers: proxyHeaders } = proxifyMetadata(
      urlObj.toString()
    );
    const response = await fetch(proxyUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...proxyHeaders
      }
    });
    if (!response.ok) {
      console.error(
        `[@dialectlabs/blinks] Failed to fetch metadata, response status: ${response.status}`
      );
      return {
        rows: []
      };
    }
    return await response.json();
  } catch (e) {
    console.error(`[@dialectlabs/blinks] Failed to fetch metadata`, e);
    return {
      rows: []
    };
  }
}

// src/service-api/useBlink.ts
var useBlink = ({ url }) => {
  const [blinkData, setBlinkData] = useState6(null);
  const [isLoading, setIsLoading] = useState6(false);
  const { blinkApiUrl, isUrlLoading } = useBlinkApiUrl(url);
  const loadBlink = useCallback4(
    async ({ abortController } = {}) => {
      if (!url || !blinkApiUrl) {
        return;
      }
      setIsLoading(true);
      try {
        const blinkData2 = await fetchBlink(blinkApiUrl);
        if (!abortController?.signal.aborted) {
          setBlinkData(blinkData2);
          setIsLoading(false);
        }
      } catch {
        if (!abortController?.signal.aborted) {
          console.error(
            "[@dialectlabs/blinks-core] Failed to fetch blink",
            blinkApiUrl
          );
          setBlinkData(null);
          setIsLoading(false);
        }
      }
    },
    [url, blinkApiUrl]
  );
  useEffect6(() => {
    const abortController = new AbortController();
    loadBlink({ abortController });
    return () => {
      abortController.abort();
    };
  }, [loadBlink]);
  return {
    // raw blink data
    blink: blinkData?.blink ?? null,
    blinkApiUrl: blinkData?.url ?? null,
    originalBlinkApiUrl: blinkApiUrl,
    // blink metadata, such as blockchain and solana actions version (if specified in blink response)
    metadata: blinkData?.metadata ?? null,
    isLoading: isLoading || isUrlLoading,
    refresh: loadBlink
  };
};
var fetchBlink = async (apiUrl, options = {}) => {
  let url;
  if (shouldIgnoreProxy(new URL(apiUrl)) || shouldPreserveOriginal(new URL(apiUrl))) {
    url = new URL(apiUrl);
  } else {
    url = new URL(`https://api.dial.to/v1/blink`);
    url.searchParams.append("apiUrl", apiUrl);
  }
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
    },
    signal: options.abortController?.signal
  });
  if (!response.ok) {
    throw new Error("Failed to fetch blink");
  }
  const blink = await response.json();
  const metadata = getBlinkSupportabilityMetadata(response);
  return { blink, metadata, url: url.toString() };
};

// src/service-api/useBlinkDataTable.ts
import { useCallback as useCallback5, useEffect as useEffect7, useState as useState7 } from "react";
var useBlinkDataTable = ({
  blinkApiUrl,
  wallet
}) => {
  const [isLoading, setLoading] = useState7(false);
  const [data, setData] = useState7(null);
  const loadBlinkDataTable = useCallback5(
    async (options = {}) => {
      if (!blinkApiUrl) {
        return;
      }
      setLoading(true);
      try {
        const data2 = await fetchBlinkDataTable(blinkApiUrl, wallet, options);
        if (!options.abortController?.signal.aborted) {
          setData(data2);
          setLoading(false);
        }
      } catch {
        if (!options.abortController?.signal.aborted) {
          console.error(
            "[@dialectlabs/blinks-core] Failed to fetch blink data table",
            blinkApiUrl
          );
          setData(null);
          setLoading(false);
        }
      }
    },
    [blinkApiUrl, wallet]
  );
  useEffect7(() => {
    const abortController = new AbortController();
    loadBlinkDataTable({ abortController });
    return () => {
      abortController.abort();
    };
  }, [loadBlinkDataTable]);
  return {
    data,
    isLoading,
    refresh: loadBlinkDataTable
  };
};
async function fetchBlinkDataTable(blinkApiUrl, wallet, options = {}) {
  const url = new URL("https://api.dial.to/v1/blink-data-table");
  url.searchParams.append("apiUrl", blinkApiUrl);
  if (wallet) {
    url.searchParams.append("account", wallet);
  }
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
    },
    signal: options.abortController?.signal
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch blink data table, url: ${blinkApiUrl}`);
  }
  return response.json();
}

// src/service-api/useBlinkList.ts
import { useCallback as useCallback6, useEffect as useEffect8, useState as useState8 } from "react";
var useBlinkList2 = ({
  id,
  wallet
}) => {
  const [loading, setLoading] = useState8(false);
  const [data, setData] = useState8(null);
  const loadBlinkList = useCallback6(
    async (options = {}) => {
      if (!id) {
        return;
      }
      setLoading(true);
      try {
        const data2 = await fetchBlinkList2(id, wallet, options);
        if (!options.abortController?.signal.aborted) {
          setData(data2);
          setLoading(false);
        }
      } catch {
        if (!options.abortController?.signal.aborted) {
          console.error(
            `[@dialectlabs/blinks-core] Failed to fetch blink list: ${id}`
          );
          setData(null);
          setLoading(false);
        }
      }
    },
    [id, wallet]
  );
  useEffect8(() => {
    const abortController = new AbortController();
    loadBlinkList({ abortController });
    return () => {
      abortController.abort();
    };
  }, [loadBlinkList]);
  return {
    loading,
    refresh: loadBlinkList,
    data
  };
};
async function fetchBlinkList2(id, wallet, options = {}) {
  const urlObj = new URL(`https://api.dial.to/v1/blink-lists/${id}`);
  if (wallet) {
    urlObj.searchParams.append("account", wallet);
  }
  const response = await fetch(urlObj, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
    },
    signal: options.abortController?.signal
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch blink list: ${id}`);
  }
  return response.json();
}

// src/service-api/useBlinkPreview.ts
import { useCallback as useCallback7, useEffect as useEffect9, useState as useState9 } from "react";
var useBlinkPreview = ({
  blinkApiUrl
}) => {
  const [isLoading, setLoading] = useState9(false);
  const [preview, setPreview] = useState9(null);
  const loadBlinkPreview = useCallback7(
    async (options = {}) => {
      if (!blinkApiUrl) {
        return;
      }
      setLoading(true);
      try {
        const data = await fetchBlinkPreview(blinkApiUrl, options);
        if (!options.abortController?.signal.aborted) {
          setPreview(data);
          setLoading(false);
        }
      } catch {
        if (!options.abortController?.signal.aborted) {
          console.error(
            "[@dialectlabs/blinks-core] Failed to fetch blink preview",
            blinkApiUrl
          );
          setPreview(null);
          setLoading(false);
        }
      }
    },
    [blinkApiUrl]
  );
  useEffect9(() => {
    const abortController = new AbortController();
    loadBlinkPreview({ abortController });
    return () => {
      abortController.abort();
    };
  }, [loadBlinkPreview]);
  return {
    preview,
    isLoading,
    refresh: loadBlinkPreview
  };
};
async function fetchBlinkPreview(blinkApiUrl, options = {}) {
  const url = new URL("https://api.dial.to/v1/blink-preview");
  url.searchParams.append("apiUrl", blinkApiUrl);
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
    },
    signal: options.abortController?.signal
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch blink preview, url: ${blinkApiUrl}`);
  }
  return response.json();
}

export {
  useBlink,
  fetchBlink,
  useBlinkDataTable,
  fetchBlinkDataTable,
  useBlinkList2 as useBlinkList,
  fetchBlinkList2 as fetchBlinkList,
  useBlinkPreview,
  fetchBlinkPreview,
  useBlinkApiUrl,
  useBlinksRegistryInterval,
  useBlink2,
  useAction,
  useBlinkList as useBlinkList2,
  fetchBlinkList as fetchBlinkList2,
  useMetadata,
  fetchMetadata
};
